import { FC } from "react";

import { useSnackbar, VariantType } from "notistack";
import { usePermissions } from "@/hooks/usePermissions";
import LoggingAPI from "@/api/logging";
import DefaultClientsForm, {
  ClientFormData,
  EditClientFormData,
} from "./DefaultClientForm";
import { updateClient } from "@/api";
import { Client } from "@/types";
import {
  FormTitle,
  FormWrapper,
} from "@/components/Forms/components/MonolithFormComponents";
import { useCustomFields } from "../hooks/useCustomFields";
import {
  processEditCustomAttributesForAPI,
  extractCustomFieldsFromFormData,
} from "./utils/customAttributeUtils";

interface EditClientFormProps {
  width?: number | undefined;
  defaultFormData: Client;
  onSubmit: () => void;
  onCancel: () => void;
}

const EditClientForm: FC<EditClientFormProps> = ({
  width,
  defaultFormData,
  onSubmit,
  onCancel,
}) => {
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const { data: customFields = [] } = useCustomFields();

  const handleSubmit = async (
    data: ClientFormData,
    diffData?: EditClientFormData
  ) => {
    try {
      // Extract custom field data from diff data
      const cleanedDiffData = extractCustomFieldsFromFormData(diffData || {});

      // Process custom attributes for editing (only changed attributes)
      const customAttributes = processEditCustomAttributesForAPI(
        diffData || {},
        customFields,
        defaultFormData.client_id,
        (defaultFormData as any).custom_attributes || []
      );

      // Include custom attributes in the update if there are any
      const updateData = {
        ...cleanedDiffData,
        ...(customAttributes.length > 0 && {
          custom_attributes: customAttributes,
        }),
      };

      await updateClient(defaultFormData.client_id, updateData);
      onSubmit?.();
      enqueueSnackbar(`Client ${data?.name || ""} Edited`, {
        variant: "success" as VariantType,
      });

      LoggingAPI.createLog({
        user_id: currentUser.user_id,
        message: `Edited Client: ${data?.name}`,
        event: "client:edit",
        object_type: "client",
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        `An error occurred while editing client ${
          data?.name || ""
        }. Please try again.`,
        { variant: "error" as VariantType }
      );
    }
  };

  return (
    <FormWrapper width={width}>
      <FormTitle>Edit Client</FormTitle>
      <DefaultClientsForm
        onSubmit={handleSubmit}
        onCancel={onCancel}
        defaultFormData={defaultFormData}
        buttonProps={{ submitText: "Edit Client" }}
      />
    </FormWrapper>
  );
};
export default EditClientForm;
